import { z } from "zod";
import { captureApiError, tagRequest } from "../monitoring/sentry.js";
import { getWebEnv } from "../env.js";

const API_URL = getWebEnv().apiUrl;

const ProblemDetailsSchema = z.object({
  type: z.string().optional(),
  title: z.string().optional(),
  status: z.number().optional(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  requestId: z.string().optional(),
  eventId: z.string().optional(),
  traceId: z.string().optional()
}).passthrough();

const SuccessEnvelopeSchema = z.object({
  ok: z.boolean().default(true),
  data: z.any().optional(),
  meta: z.object({
    requestId: z.string().optional()
  }).default({})
}).passthrough();

export class ApiError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = "ApiError";
    this.status = options.status ?? 500;
    this.requestId = options.requestId;
    this.payload = options.payload;
  }
}

async function readJson(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function request(path, options = {}) {
  const url = `${API_URL.replace(/\/$/, "")}${path}`;

  const token = typeof window !== "undefined" ? localStorage.getItem("break_agency_jwt_token") : null;
  const clientRequestId = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : null;

  let res;
  try {
    res = await fetch(url, {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(clientRequestId ? { "X-Request-Id": clientRequestId } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers
      },
      ...options
    });
  } catch (error) {
    // Fallback: when cross-origin API endpoint fails at network layer (e.g., bad env URL/CORS/mixed-content),
    // retry against same-origin path so reverse-proxy deployments continue working.
    if (typeof window !== "undefined") {
      try {
        res = await fetch(path, {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(clientRequestId ? { "X-Request-Id": clientRequestId } : {}),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...options.headers
          },
          ...options
        });
      } catch {
        // fall through to original network error handling
      }
    }

    if (res) {
      // Continue with normal non-ok handling below.
    } else {
    const apiError = new ApiError("Network error", { cause: error });
    captureApiError(apiError, { path, method: options.method ?? "GET" });
    throw apiError;
    }
  }

  const requestId = res.headers.get("x-request-id") ?? res.headers.get("X-Request-Id") ?? clientRequestId;
  const body = await readJson(res);

  if (!res.ok) {
    const parsed = ProblemDetailsSchema.safeParse(body);
    const message =
      parsed.success
        ? parsed.data.detail || parsed.data.title || "Request failed"
        : res.statusText || "Request failed";

    const apiError = new ApiError(message, {
      status: res.status,
      requestId: parsed.success ? parsed.data.requestId ?? requestId : requestId,
      payload: parsed.success ? parsed.data : body
    });

    captureApiError(apiError, {
      path,
      method: options.method ?? "GET",
      requestId: apiError.requestId,
      response: body
    });

    throw apiError;
  }

  if (res.status === 204 || body === null) {
    if (requestId) tagRequest(requestId);
    return null;
  }

  const envelope = SuccessEnvelopeSchema.safeParse(body);
  if (envelope.success) {
    const id = envelope.data.meta.requestId ?? requestId;
    if (id) tagRequest(id);
    return envelope.data.data ?? body;
  }

  if (typeof body === "string") {
    const trimmed = body.trim();
    const looksLikeHtml = /^<!doctype html/i.test(trimmed) || /^<html/i.test(trimmed) || /^<head/i.test(trimmed);
    if (looksLikeHtml) {
      const apiError = new ApiError("API returned HTML instead of JSON", {
        status: 502,
        requestId,
        payload: { snippet: trimmed.slice(0, 180) }
      });
      captureApiError(apiError, {
        path,
        method: options.method ?? "GET",
        requestId,
        response: trimmed.slice(0, 500)
      });
      throw apiError;
    }
  }

  if (requestId) tagRequest(requestId);
  return body;
}

export function getListings(params = {}) {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.q) query.set("q", params.q);
  if (params.page) query.set("page", String(params.page));
  if (params.pageSize) query.set("pageSize", String(params.pageSize));
  const qs = query.toString();
  return request(`/api/listings${qs ? `?${qs}` : ""}`);
}

export function getPublicListings(params = {}) {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.q) query.set("q", params.q);
  if (params.page) query.set("page", String(params.page));
  if (params.pageSize) query.set("pageSize", String(params.pageSize));
  const qs = query.toString();
  return request(`/public/listings${qs ? `?${qs}` : ""}`);
}

export function getListing(id) {
  return request(`/api/listings/${id}`);
}

export function getListingViewings(id) {
  return request(`/api/listings/${id}/viewings`);
}

export function createListing(payload) {
  return request("/api/listings", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function updateListing(id, payload) {
  return request(`/api/listings/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

export function getContacts(params = {}) {
  const query = new URLSearchParams();
  if (params.type) query.set("type", params.type);
  if (params.q) query.set("q", params.q);
  if (params.page !== undefined) query.set("page", String(params.page));
  if (params.pageSize !== undefined) query.set("pageSize", String(params.pageSize));
  const qs = query.toString();
  return request(`/api/contacts${qs ? `?${qs}` : ""}`);
}

export function getDeletedContacts(params = {}) {
  const query = new URLSearchParams();
  if (params.sinceDays !== undefined) query.set("sinceDays", String(params.sinceDays));
  const qs = query.toString();
  return request(`/api/admin/deleted-contacts${qs ? `?${qs}` : ""}`);
}

export function getContactDetail(id) {
  return request(`/api/contacts/${id}`);
}

export function createContact(payload) {
  return request("/api/contacts", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function updateContact(id, payload) {
  return request(`/api/contacts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

export function deleteContact(id) {
  return request(`/api/contacts/${id}`, {
    method: "DELETE"
  });
}

export function restoreContact(id) {
  return request(`/api/contacts/${id}/restore`, { method: "POST" });
}

export function addContactListing(contactId, listingId) {
  return request(`/api/contacts/${contactId}/listings`, {
    method: "POST",
    body: JSON.stringify({ listingId })
  });
}

export function removeContactListing(contactId, listingId) {
  return request(`/api/contacts/${contactId}/listings/${listingId}`, {
    method: "DELETE"
  });
}

export function getViewings(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) query.set(key, String(value));
  });
  const qs = query.toString();
  return request(`/api/viewings${qs ? `?${qs}` : ""}`);
}

export function getInterestLeads() {
  return request("/api/interest");
}

export function getAnalyticsPipeline(params = {}) {
  const query = new URLSearchParams();
  if (params.start) query.set("start", params.start);
  if (params.end) query.set("end", params.end);
  if (params.contactType) query.set("contactType", params.contactType);
  if (params.listingStatus) query.set("listingStatus", params.listingStatus);
  if (params.source) query.set("source", params.source);
  const qs = query.toString();
  return request(`/api/analytics/pipeline${qs ? `?${qs}` : ""}`);
}

export function getAnalyticsActivity(params = {}) {
  const query = new URLSearchParams();
  if (params.start) query.set("start", params.start);
  if (params.end) query.set("end", params.end);
  const qs = query.toString();
  return request(`/api/analytics/activity${qs ? `?${qs}` : ""}`);
}

export function getAnalyticsViewings(params = {}) {
  const query = new URLSearchParams();
  if (params.start) query.set("start", params.start);
  if (params.end) query.set("end", params.end);
  const qs = query.toString();
  return request(`/api/analytics/viewings${qs ? `?${qs}` : ""}`);
}

export function getAnalyticsAgents(params = {}) {
  const query = new URLSearchParams();
  if (params.start) query.set("start", params.start);
  if (params.end) query.set("end", params.end);
  const qs = query.toString();
  return request(`/api/analytics/agents${qs ? `?${qs}` : ""}`);
}

export function getGoogleCalendarUrl() {
  return request("/api/google/calendar-url");
}

export function connectGoogleCalendar(payload) {
  return request("/api/google/calendar-token", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function getGoogleCalendarStatus() {
  return request("/api/google/calendar-status");
}

export function getProfile() {
  return request("/api/profile/me");
}

export function updateProfile(payload = {}) {
  return request("/api/profile/me", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function postAIChat(messages = []) {
  return request("/api/ai/chat", {
    method: "POST",
    body: JSON.stringify({ messages })
  }).catch(err => {
    // For AI chat, we want to return error details in response format
    // so the UI can handle unavailability gracefully
    const status = err?.status;
    if (status === 401) {
      return {
        error: "auth_required",
        message: err.message,
        requestId: err.requestId
      };
    }
    if (status === 503 || status === 429) {
      return {
        error: status === 503 ? "ai_unavailable" : "rate_limit_exceeded",
        message: err.message,
        requestId: err.requestId
      };
    }
    if (status >= 500 || status === 0 || status === undefined) {
      return {
        error: "ai_unavailable",
        message: err.message || "AI service is temporarily unavailable.",
        requestId: err.requestId
      };
    }
    return {
      error: "ai_unavailable",
      message: err.message || "AI chat is currently unavailable.",
      requestId: err.requestId
    };
  });
}

export function getAIRecommendations(params = {}) {
  const query = new URLSearchParams();
  if (params.limit) query.set("limit", String(params.limit));
  if (params.basedOn) query.set("basedOn", JSON.stringify(params.basedOn));
  const qs = query.toString();
  return request(`/api/ai/recommendations${qs ? `?${qs}` : ""}`);
}

export function postAIRecommendationFeedback(recommendationId, payload) {
  return request(`/api/ai/recommendations/${recommendationId}/feedback`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function postAIValuation(payload) {
  return request("/api/ai/valuation", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function postAIValuationEscalation(id, payload) {
  return request(`/api/ai/valuation/${id}/escalate`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function getAIInteractions(params = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.pageSize) query.set("pageSize", String(params.pageSize));
  if (params.feature) query.set("feature", params.feature);
  const qs = query.toString();
  return request(`/api/ai/interactions${qs ? `?${qs}` : ""}`);
}

export function getAICostBreakdown(params = {}) {
  const query = new URLSearchParams();
  if (params.startDate) query.set("startDate", params.startDate);
  if (params.endDate) query.set("endDate", params.endDate);
  const qs = query.toString();
  return request(`/api/ai/cost-breakdown${qs ? `?${qs}` : ""}`);
}

export function getOffers(params = {}) {
  const query = new URLSearchParams();
  if (params.listingId) query.set("listingId", params.listingId);
  if (params.buyerContactId) query.set("buyerContactId", params.buyerContactId);
  if (params.status) query.set("status", params.status);
  if (params.page) query.set("page", String(params.page));
  if (params.pageSize) query.set("pageSize", String(params.pageSize));
  const qs = query.toString();
  return request(`/api/offers${qs ? `?${qs}` : ""}`);
}

export function getOffer(id) {
  return request(`/api/offers/${id}`);
}

export function createOffer(payload) {
  return request("/api/offers", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function updateOffer(id, payload) {
  return request(`/api/offers/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

export function updateOfferStatus(id, status) {
  return request(`/api/offers/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status })
  });
}

export function deleteOffer(id) {
  return request(`/api/offers/${id}`, {
    method: "DELETE"
  });
}

export function submitValuationRequest(payload) {
  return request("/api/valuations", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function registerUser(payload) {
  return request("/api/auth/register", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function loginUser(payload) {
  return request("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function loginWithGoogleToken(payload) {
  return request("/api/auth/google", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function getCurrentUser() {
  return request("/api/auth/me");
}

// Saved Searches
export function getSavedSearches() {
  return request("/api/saved-searches");
}

export function createSavedSearch(filters, name) {
  return request("/api/saved-searches", {
    method: "POST",
    body: JSON.stringify({ filters, name })
  });
}

export function deleteSavedSearch(id) {
  return request(`/api/saved-searches/${id}`, {
    method: "DELETE"
  });
}

// Viewing Feedback
export function submitViewingFeedback(viewingId, feedback) {
  return request(`/api/viewings/${viewingId}/feedback`, {
    method: "PUT",
    body: JSON.stringify({ feedback })
  });
}

// Create Viewing
export function createViewing(payload) {
  return request("/api/viewings", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

// AML
export function getAmlPendingReview() {
  return request("/api/aml/pending-review");
}

export function approveAmlDocument(documentId, payload = {}) {
  return request(`/api/aml/approve/${documentId}`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function rejectAmlDocument(documentId, payload) {
  return request(`/api/aml/reject/${documentId}`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

// Admin audit logs
export function getAuditLogs(params = {}) {
  const query = new URLSearchParams();
  if (params.userId) query.set("userId", params.userId);
  if (params.action) query.set("action", params.action);
  if (params.entity) query.set("entity", params.entity);
  if (params.start) query.set("start", params.start);
  if (params.end) query.set("end", params.end);
  if (params.page) query.set("page", String(params.page));
  if (params.pageSize) query.set("pageSize", String(params.pageSize));
  const qs = query.toString();
  return request(`/api/admin/audit-logs${qs ? `?${qs}` : ""}`);
}

// GDPR
export function downloadMyData() {
  return request("/api/gdpr/download-my-data");
}

export function deleteMyAccount(payload) {
  return request("/api/gdpr/delete-my-account", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function updateConsent(payload) {
  return request("/api/gdpr/update-consent", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

// Payments
export function getPaymentHistory(params = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.pageSize) query.set("pageSize", String(params.pageSize));
  const qs = query.toString();
  return request(`/api/payments/history${qs ? `?${qs}` : ""}`);
}
